/**
 * Validate Slack Token Schema
 *
 * Validates a Slack refresh token and returns workspace info.
 * Called by frontend during automatic bot creation flow.
 *
 * Users only need to provide the refresh token - we derive the access token.
 */

import { z } from "zod";

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const ValidateSlackTokenBodySchema = z.object({
  /** Refresh token (xoxe-1-...) - we derive access token from this */
  refreshToken: z.string().min(1, "Refresh token is required")
});

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

export const ValidateSlackTokenResponseSchema = z.object({
  teamId: z.string(),
  teamName: z.string(),
  isEnterprise: z.boolean(),
  enterpriseId: z.string().optional(),
  /** The derived access token (for immediate use if needed) */
  configToken: z.string(),
  /**
   * The rotated refresh token issued by tooling.tokens.rotate.
   * Slack single-use rotation: the original token is consumed on every call.
   * The frontend MUST use this token (not the original) in subsequent calls
   * (e.g. connectSlackWorkspace) to avoid invalid_refresh_token errors.
   */
  newRefreshToken: z.string()
});

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type ValidateSlackTokenBody = z.infer<typeof ValidateSlackTokenBodySchema>;
export type ValidateSlackTokenResponse = z.infer<typeof ValidateSlackTokenResponseSchema>;
